// src/pages/LoginPage.jsx

import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { HiOutlineLockClosed, HiOutlineMail } from 'react-icons/hi';
import logo from '../assets/logo_logo.svg';
import LanguageToggle from '../components/ui/LanguageToggle';
import ThemeToggle from '../components/ui/ThemeToggle';

const LoginPage = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const formData = new FormData(e.currentTarget);
    const emailValue = String(formData.get('email') || '').trim();
    const passwordValue = String(formData.get('password') || '');

    setEmail(emailValue);
    setPassword(passwordValue);

    try {
      await login(emailValue, passwordValue);
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || t('auth.login.unexpected_error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 text-white">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(37,99,235,0.18),_transparent_45%)]"></div>
        <div className="absolute -top-20 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-blue-600/20 blur-3xl"></div>
        <div className="absolute bottom-0 right-0 h-64 w-64 rounded-full bg-slate-700/30 blur-3xl"></div>
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(2,6,23,0.85),rgba(2,6,23,0.98))]"></div>
      </div>

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="rounded-3xl border border-slate-700/70 bg-slate-900/70 shadow-2xl shadow-blue-950/40 backdrop-blur">
            <div className="px-6 pt-8 pb-7 sm:px-8">
              <div className="flex items-center gap-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-slate-700/80 bg-slate-800/80">
                  <img src={logo} alt="Hedger logo" className="h-9 w-9" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold tracking-tight text-white">Hedger</h1>
                  <p className="text-sm text-slate-400">{t('auth.login.subtitle')}</p>
                </div>
              </div>

              <form className="mt-7 space-y-5" onSubmit={handleSubmit}>
                <div className="relative">
                  <HiOutlineMail className="absolute top-1/2 left-4 -translate-y-1/2 text-slate-400" />
                  <input
                    id="email-address"
                    name="email"
                    type="email"
                    autoComplete="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    inputMode="email"
                    required
                    className="w-full rounded-xl border border-slate-700/80 bg-slate-800/70 py-3 pl-11 pr-4 text-white placeholder:text-slate-500 focus:border-blue-500/70 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                    placeholder={t('auth.common.email_placeholder')}
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </div>

                <div className="relative">
                  <HiOutlineLockClosed className="absolute top-1/2 left-4 -translate-y-1/2 text-slate-400" />
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    className="w-full rounded-xl border border-slate-700/80 bg-slate-800/70 py-3 pl-11 pr-4 text-white placeholder:text-slate-500 focus:border-blue-500/70 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                    placeholder={t('auth.common.password_placeholder')}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                </div>

                <div className="flex items-center justify-end text-sm">
                  <Link to="/request-password-reset" className="font-medium text-blue-400 hover:text-blue-300">
                    {t('auth.login.forgot_password')}
                  </Link>
                </div>

                {error && (
                  <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full rounded-xl bg-gradient-to-r from-blue-600 via-blue-500 to-sky-500 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-900/30 transition-all hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-blue-400/40 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {isLoading ? t('auth.login.signing_in') : t('auth.login.sign_in')}
                </button>
              </form>

              <div className="mt-6 border-t border-slate-800/80 pt-5 text-center text-sm text-slate-400">
                {t('auth.login.no_account')}{' '}
                <Link to="/register" className="font-medium text-blue-400 hover:text-blue-300">
                  {t('auth.login.register_here')}
                </Link>
              </div>
            </div>
          </div>

          <p className="mt-6 text-center text-xs text-slate-500">
            {t('auth.login.footer')}
          </p>
        </div>
      </div>

      <div className="fixed bottom-3 right-3 z-20 flex w-44 gap-2 sm:bottom-4 sm:right-4 sm:w-52">
        <ThemeToggle shortLabels size="sm" className="shadow-lg shadow-black/35" />
        <LanguageToggle shortLabels size="sm" className="shadow-lg shadow-black/35" />
      </div>
    </div>
  );
};

export default LoginPage;
